<template>
  <v-flex
    id="baseInfo"
    class="question"
  >
    <p><span class="sz_red--text">*</span> {{ $t('sheet.fields_required') }}</p>
    <p class="f-1">
      {{ $t('warehouses.base_data.hint_1') }}
      <a
        :href="warehouseTutorialLink"
        target="_blank"
      >
        {{ $t('warehouses.base_data.hint_link') }}
      </a>
      {{ $t('warehouses.supporting_base_info.hint_2') }}
    </p>
    <h2 class="header">
      {{ $t('warehouses.base_data.title') }}
    </h2>
    <ul class="questions-list">
      <li class="mt-4">
        <p class="header required">
          {{ question1.description }}
        </p>
        <v-radio-group
          v-model="supportingWarehouseType"
          v-validate="validationRules.get('supportingWarehouseType')"
          :error-messages="errors.collect('supportingWarehouseType')"
          :data-vv-as="' '"
          data-vv-name="supportingWarehouseType"
          required
        >
          <v-radio
            v-for="(item, index) in question1.resources"
            :key="index"
            :label="item.description"
            :value="item.id"
          />
        </v-radio-group>
      </li>
      <li class="mt-4">
        <p class="header required">
          {{ $t('warehouses.supporting_base_info.question_2') }}
        </p>
        <v-checkbox
          v-for="(item, index) in question2Params"
          :key="index"
          v-model="baseParameters"
          v-validate="validationRules.get('addWarehouseBaseParameters')"
          multiple
          :label="item.description"
          :value="item.id"
          :data-vv-as="' '"
          data-vv-name="baseParameters"
          required
        />
        <div
          v-if="errors.has('baseParameters')"
          class="v-message my-4 text-xs-center"
        >
          <div class="v-messages__message">
            {{ errors.first('baseParameters') }}
          </div>
        </div>
      </li>
      <li
        v-if="showWayForWarehouseOperation"
        class="mt-4"
      >
        <p class="header required custom lA">
          {{ $t('warehouses.supporting_base_info.question_2a') }}
        </p>
        <v-textarea
          v-model="wayForWarehouseOperation"
          v-validate="validationRules.get('wayForWarehouseOperation')"
          :placeholder="$t('sheet.enter_text')"
          :error-messages="errors.collect('wayForWarehouseOperation')"
          :data-vv-as="' '"
          data-vv-name="wayForWarehouseOperation"
          required
          auto-grow
        />
      </li>
      <li
        v-if="supportingWarehouseType === 334"
        class="mt-4"
      >
        <p class="header required">
          {{ $t('warehouses.supporting_base_info.question_3') }}
        </p>
        <ConfirmButtons
          v-model="isLocationFree"
          v-validate="'required'"
          string
          :error-messages="errors.collect('isLocationFree')"
          :data-vv-as="' '"
          :data-vv-name="'isLocationFree'"
        />
      </li>
      <li
        v-if="supportingWarehouseType === 334 && isLocationFree === 'no'"
        class="mt-4"
      >
        <p class="f-14 fw-400">
          {{ $t('warehouses.supporting_base_info.hint') }}
        </p>
        <p class="header required custom lA mb-0">
          {{ $t('warehouses.supporting_base_info.question_3a') }}
        </p>
        <v-textarea
          v-model="whyThisLocation"
          v-validate="validationRules.get('whyThisLocation')"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('whyThisLocation')"
          :data-vv-as="' '"
          data-vv-name="whyThisLocation"
          required
          auto-grow
        />
      </li>
      <li
        v-if="supportingWarehouseType === 334 && isLocationFree === 'no'"
        class="mt-4"
      >
        <p class="header required custom lB mb-0">
          {{ $t('warehouses.supporting_base_info.question_3b') }}
        </p>
        <v-flex
          lg4
          md5
        >
          <v-text-field
            v-model="cost"
            v-validate="costValidation"
            :placeholder="$t('warehouses.supporting_base_info.labels.cost')"
            :error-messages="errors.collect('cost')"
            :data-vv-as="' '"
            data-vv-name="cost"
            required
            :suffix="'PLN  /  h'"
            :disabled="costDisabled"
            inputmode="decimal"
          />
        </v-flex>
        <v-flex xs12>
          <v-textarea
            v-model="barter"
            v-validate="barterValidation"
            :placeholder="$t('warehouses.supporting_base_info.labels.barter')"
            :error-messages="errors.collect('barter')"
            :data-vv-as="' '"
            data-vv-name="barter"
            required
            auto-grow
            :disabled="barterDisabled"
          />
        </v-flex>
      </li>
    </ul>
    <v-divider class="my-4" />
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import links from '@/constants/links';

import validationRules from '@/constants/validationRules';
import { createHelpers } from 'vuex-map-fields';

import find from 'lodash.find';

const { mapFields } = createHelpers({
    getterType: 'warehouses/getSupportingBaseInfoFields',
    mutationType: 'warehouses/updateSupportingBaseInfoField'
});

export default {
    name: 'SupportingBaseInfo',
    components: {
        ConfirmButtons
    },
    inject: ['$validator'],
    data () {
        return {
            validationRules,
            warehouseTutorialLink: links.WAREHOUSE_TUTORIAL
        };
    },
    computed: {
        ...mapFields([
            'supportingWarehouseType',
            'baseParameters',
            'showWayForWarehouseOperation',
            'wayForWarehouseOperation',
            'isLocationFree',
            'whyThisLocation',
            'cost',
            'barter'
        ]),
        question2Params () {
            return this.supportingWarehouseType === 333
                ? this.provinceQuestion2.resources
                : this.question2.resources;
        },
        provinceQuestion2 () {
            return this.resource.find((el) => el.id === 1012);
        },
        costValidation () {
            return this.barter
                ? this.validationRules.get('addWarehouseCostsNonRequired')
                : this.validationRules.get('addWarehouseCosts');
        },
        barterValidation () {
            return this.cost
                ? this.validationRules.get('addWarehouseBarterNonRequired')
                : this.validationRules.get('addWarehouseBarter');
        },
        resource () {
            return this.$store.getters['warehouses/getResource'];
        },
        question1 () {
            return this.resource.find((el) => el.id === 1010);
        },
        question2 () {
            return this.resource.find((el) => el.id === 1011);
        },
        costDisabled () {
            return this.barter != null && this.barter !== '';
        },
        barterDisabled () {
            return this.cost != null && this.cost !== '';
        }
    },
    watch: {
        supportingWarehouseType: {
            handler () {
                this.$nextTick(() => {
                    this.baseParameters = [];
                    const errorField = find(this.$validator.errors.items, ['field', 'baseParameters'])
                        ? this.$validator.fields.find({ name: 'baseParameters' })
                        : false;
                    if (errorField) {
                        errorField.reset();
                        this.$validator.reset(errorField);
                    }
                });
                this.wayForWarehouseOperation = null;
            }
        },
        baseParameters: {
            handler () {
                this.checkShowQuestion2a();
            },
            immediate: true
        },
        question2Params: {
            handler () {
                this.checkShowQuestion2a();
            },
            immediate: true,
            deep: true
        },
        showWayForWarehouseOperation: {
            handler (val) {
                if (val !== true) {
                    this.wayForWarehouseOperation = null;
                }
            },
            immediate: true
        }
    },
    created () {
        this.baseParameters = this.baseParameters.filter((el) => {
            return this.question2Params.some((x) => el === x.id);
        });
    },
    methods: {
        checkShowQuestion2a () {
            this.showWayForWarehouseOperation = this.question2Params.length !== this.baseParameters.length;
        }
    }
};
</script>

<style scoped></style>
